import React, { useState } from 'react';
import {
  Box, Button, TextField, MenuItem, IconButton, Tooltip, Chip, Typography
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEqpListStatusChip, formatAgo } from './eqpState';
import EqpDeleteConfirmDialog from './EqpDeleteConfirmDialog';
const EqpListPanel = ({
  list, loading,
  searchKeyword, searchEqpTyp,
  onSearchKeywordChange, onSearchEqpTypChange,
  onAdd, onRowClick, onDelete,
  eqpTypeOptions = [],
  canManage = true,
}) => {
  const eqpTypeLabelMap = Object.fromEntries(eqpTypeOptions.map(o => [o.slcCd, o.itemNm]));
  // 목록 휴지통 아이콘도 즉시 삭제하지 않고 확인창을 먼저 띄운다(2026-10-19) - 대상 행을 들고 있는다.
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteConfirm = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (target) await onDelete(target.eqpUuid);
  };

  const columns = [
    { field: 'eqpCd',     headerName: '장비코드', width: 160 },
    { field: 'eqpNm',     headerName: '장비명',   flex: 1, minWidth: 150 },
    {
      field: 'eqpTyp', headerName: '분류', width: 110,
      renderCell: ({ value }) => value ? (eqpTypeLabelMap[value] || value) : '-',
    },
    {
      // headerName에 "상태"가 들어가야 ResponsiveDataGrid 모바일 카드에서 배지로 자동 선택된다(BADGE_HINT).
      field: 'runSt', headerName: '상태', width: 150, sortable: false,
      renderCell: ({ row }) => {
        const { label, color } = getEqpListStatusChip(row);
        const ago = formatAgo(row.stateUpdDt);
        const showPrg = row.runSt === 'RUNNING' && !row.stale && row.prgNo;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5, minWidth: 0 }}>
            <Chip size="small" color={color} label={label} sx={{ alignSelf: 'flex-start' }} />
            {showPrg && (
              <Typography variant="caption" color="text.secondary" noWrap>{row.prgNo}</Typography>
            )}
            {ago && (
              <Typography variant="caption" color="text.secondary" noWrap>{ago}</Typography>
            )}
          </Box>
        );
      },
    },
    { field: 'maker',     headerName: '제조사',   width: 120 },
    { field: 'modelNm',   headerName: '모델명',   width: 140 },
    { field: 'installDt', headerName: '설치일',   width: 110 },
    { field: 'location',  headerName: '설치위치', width: 140 },
    { field: 'mgrUsrNm',  headerName: '담당자',   width: 100 },
    {
      field: 'connUrl', headerName: '접속주소', width: 160,
      renderCell: ({ value }) => value || '-',
    },
    {
      field: 'maintCnt', headerName: '이력건수', width: 90,
      renderCell: ({ value }) => value ?? 0,
    },
    { field: 'lastMaintDt', headerName: '최근이력', width: 140 },
    ...(canManage ? [{
      field: 'actions', headerName: '', width: 60, sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="삭제">
          <IconButton
            size="small" color="error"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }] : []),
  ];

  return (
    <Box>
      {/* 검색 필터 - xs: 검색어+분류 한 줄, 등록 버튼은 다음 줄 */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box
          sx={{
            order: { xs: 1, sm: 0 },
            flexBasis: { xs: '100%', sm: 'auto' },
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          <TextField
            label="장비명/코드 검색" size="small"
            value={searchKeyword}
            onChange={(e) => onSearchKeywordChange(e.target.value)}
            sx={{ width: { xs: '0', sm: 200 }, flex: { xs: 1, sm: '0 0 auto' }, minWidth: 0 }}
          />
          <TextField
            select label="분류" size="small"
            value={searchEqpTyp}
            onChange={(e) => onSearchEqpTypChange(e.target.value)}
            sx={{ width: { xs: '0', sm: 130 }, flex: { xs: '0 0 35%', sm: '0 0 auto' }, minWidth: 0 }}
          >
            <MenuItem value="">전체</MenuItem>
            {eqpTypeOptions.map(({ slcCd, itemNm }) => (
              <MenuItem key={slcCd} value={slcCd}>{itemNm}</MenuItem>
            ))}
          </TextField>
        </Box>
        {canManage && (
          <Box sx={{ order: { xs: 2, sm: 0 }, flexBasis: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 'auto' } }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
              장비 등록
            </Button>
          </Box>
        )}
      </Box>

      {/* DataGrid */}
      <DataGrid
        rows={list}
        columns={columns}
        getRowId={(row) => row.eqpUuid}
        loading={loading}
        autoHeight
        disableColumnMenu
        pageSizeOptions={[20, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        onRowClick={({ row }) => onRowClick(row)}
        sx={{
          cursor: 'pointer',
          '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
        }}
      />

      <EqpDeleteConfirmDialog
        open={Boolean(deleteTarget)}
        eqpNm={deleteTarget?.eqpNm}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default EqpListPanel;
